// StaffGauge — vertical striped staff beside the big CFS number on
// the river page hero. The physical enamel-staff gauge crossed with
// a UI status indicator: the optimal range renders as a teal band,
// the current reading as a glowing horizontal needle.
//
// Why a component, not inline:
//   - The 3 SAFE items (token cutover, type floor, instruments) need
//     to be visible to Jake on Pine. A standalone component lets me
//     drop it in one place without touching the rest of the hero.
//   - Reused on the Storm Tracker panel (mockup 03) and the embed
//     widget. One source of truth for the visual language.
//
// Visual:
//   - 32px wide × 120px tall on desktop, 24px × 80px on mobile.
//   - Background: --water-1 surface with --blue-low horizontal
//     tick stripes (the enamel staff look).
//   - Optimal band: --teal-dim backdrop with --teal edge ticks.
//   - Current reading: --teal needle (3px horizontal bar) with a
//     1px --teal-deep underglow.
//   - Out-of-range (above max or below min): --amber needle.
//   - Out-of-range by a lot (flood): --red needle.
//
// Uses the new Confluence tokens (--water-*, --teal, --blue-hi/mid/low,
// --amber, --red, --r-s, --s-2). Falls back to legacy --rv/--wt
// colors when running on a build that doesn't have the new tokens
// yet, so this is safe to ship before the token cutover.

'use client'

import type { FlowCondition } from '@/types'

interface Props {
  currentCfs: number | null
  optMin: number | null     // low end of optimal band, in CFS
  optMax: number | null     // high end of optimal band, in CFS
  condition: FlowCondition
  // Visual y-axis extent. If omitted, inferred from currentCfs ± 50%
  // with the optimal range as the anchor (band always centered).
  yMin?: number
  yMax?: number
}

function inferExtent(c: number | null, lo: number | null, hi: number | null): { yMin: number; yMax: number } {
  if (lo != null && hi != null && hi > lo) {
    // Anchor on the optimal band, expand 30% on each side so the
    // band is always in the middle 40% of the gauge.
    const band = hi - lo
    const pad = band * 0.3
    return { yMin: Math.max(0, lo - pad), yHi: hi + pad } as any
  }
  if (c != null && c > 0) {
    return { yMin: 0, yMax: c * 1.8 }
  }
  return { yMin: 0, yMax: 1000 }
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}

function needleColor(cfs: number | null, lo: number | null, hi: number | null, condition: FlowCondition): { bg: string; glow: string } {
  if (cfs == null) return { bg: 'var(--tx3, #aaa99a)', glow: 'transparent' }
  if (lo != null && cfs < lo * 0.7) return { bg: 'var(--red, #A32D2D)', glow: 'rgba(214, 90, 80, .35)' }
  if (hi != null && cfs > hi * 1.3) return { bg: 'var(--red, #A32D2D)', glow: 'rgba(214, 90, 80, .35)' }
  if (hi != null && cfs > hi) return { bg: 'var(--amber, #BA7517)', glow: 'rgba(186, 117, 23, .35)' }
  if (lo != null && cfs < lo) return { bg: 'var(--amber, #BA7517)', glow: 'rgba(186, 117, 23, .35)' }
  return { bg: 'var(--teal, #1D9E75)', glow: 'rgba(44, 181, 138, .45)' }
}

export default function StaffGauge({ currentCfs, optMin, optMax, condition, yMin, yMax }: Props) {
  const ext = inferExtent(currentCfs, optMin, optMax)
  const yLo = yMin ?? ext.yMin
  const yHi = yMax ?? (ext as any).yHi ?? ext.yMax ?? 1000
  const ySpan = Math.max(yHi - yLo, 1)

  // Pixels (SVG viewBox units — 26×96)
  // Fable 5 §2: the staff is a vertical striped board. Per the
  // mockup .staff{width:26px}, the width is 26px. Tall enough to
  // read as an instrument, short enough not to dominate a 52px
  // CFS numeral. Mobile drops to 24×72 via the @media block below.
  const W = 26, H = 96
  // Map CFS value → vertical position (top = max, bottom = min)
  const yOf = (v: number) => clamp(2 + (1 - (v - yLo) / ySpan) * (H - 4), 2, H - 2)

  // Band rect (only if both opt endpoints known)
  const bandTop = optMax != null ? yOf(optMax) : null
  const bandBot = optMin != null ? yOf(optMin) : null
  const showBand = bandTop != null && bandBot != null && bandBot > bandTop

  // Needle (only if current value known)
  const needleY = currentCfs != null ? yOf(currentCfs) : null
  const col = needleColor(currentCfs, optMin, optMax, condition)

  // Tick marks every ~10% of the y-axis
  const ticks: number[] = []
  for (let i = 0; i <= 10; i++) ticks.push(yLo + (i / 10) * ySpan)

  return (
    <div
      className="staff-gauge"
      title={currentCfs != null ? `${Math.round(currentCfs).toLocaleString()} cfs${optMin != null && optMax != null ? ` (optimal ${optMin.toLocaleString()}–${optMax.toLocaleString()})` : ''}` : 'No data'}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        width: `${W}px`,
        height: `${H}px`,
        margin: '0 6px',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        style={{ display: 'block', overflow: 'visible' }}
        aria-hidden="true"
      >
        {/* Definitions — needle glow uses a filter so the underglow
            renders cheaply on mobile (no per-frame JS). */}
        <defs>
          <filter id="staff-gauge-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Enamel-staff horizontal stripe pattern. 2px stripes,
              --blue-low on --water-1 — the physical gauge look. */}
          <pattern id="staff-stripes" x="0" y="0" width="4" height="2" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="4" height="1" fill="var(--water-1, #071C33)" />
            <rect x="0" y="1" width="4" height="1" fill="var(--blue-low, #153F6B)" opacity="0.45" />
          </pattern>
        </defs>

        {/* Staff body — enamel stripes on the dark surface */}
        <rect x="0" y="0" width={W} height={H} rx="2" fill="url(#staff-stripes)" />
        {/* Outer hairline so the staff reads as a discrete object on
            the dark hero background */}
        <rect x="0.5" y="0.5" width={W - 1} height={H - 1} rx="2"
              fill="none" stroke="var(--hairline, rgba(160,193,224,.20))" strokeWidth="1" />

        {/* Tick marks — every 10% of the y-axis, subtle hairlines */}
        {ticks.map((v, i) => (
          <line
            key={i}
            x1={0} x2={4}
            y1={yOf(v)} y2={yOf(v)}
            stroke="var(--hairline, rgba(160,193,224,.20))" strokeWidth="0.6"
          />
        ))}

        {/* Optimal range band — teal backdrop, deeper teal edge ticks */}
        {showBand && (
          <g>
            <rect
              x="2" y={bandTop!}
              width={W - 4}
              height={bandBot! - bandTop!}
              fill="var(--teal-dim, rgba(29,158,117,.18))"
              stroke="var(--teal, #1D9E75)" strokeWidth="0.8" strokeOpacity="0.55"
            />
          </g>
        )}

        {/* Needle — horizontal bar, extends slightly past the staff
            on the right so it reads as an indicator, not a tick. */}
        {needleY != null && (
          <g filter="url(#staff-gauge-glow)">
            <line
              x1={-2} x2={W + 4}
              y1={needleY} y2={needleY}
              stroke={col.bg} strokeWidth="3" strokeLinecap="round"
            />
            {/* Inner highlight — slightly lighter, gives the bar depth */}
            <line
              x1={-2} x2={W + 4}
              y1={needleY - 0.6} y2={needleY - 0.6}
              stroke="rgba(255,255,255,0.45)" strokeWidth="0.6" strokeLinecap="round"
            />
          </g>
        )}
      </svg>

      {/* Reduced-motion: kill the pulse, keep the static glow */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .staff-gauge svg * { transition: none !important; animation: none !important; }
        }
        @media (max-width: 640px) {
          .staff-gauge { width: 24px !important; height: 72px !important; margin: 0 4px !important; }
          .staff-gauge svg { width: 24px !important; height: 72px !important; }
        }
      `}</style>
    </div>
  )
}
